import { CheckCircle, Loader2, RefreshCw, XCircle } from 'lucide-react';
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { useMergeUsersJob, useMergeUsersRetryMutation } from '@/hooks/users/useMergeUsers';

interface MergeUsersProgressDialogProps {
  isOpen: boolean;
  jobId: number | null;
  onClose: () => void;
}

export const MergeUsersProgressDialog = ({ isOpen, jobId, onClose }: MergeUsersProgressDialogProps) => {
  const { data: jobStatus } = useMergeUsersJob(jobId, isOpen);
  const retryMutation = useMergeUsersRetryMutation();

  if (!jobStatus) return null;

  const progress =
    jobStatus.progress.total > 0
      ? ((jobStatus.progress.completed + jobStatus.progress.failed) / jobStatus.progress.total) * 100
      : 0;

  const isComplete = jobStatus.job.status === 'completed' || jobStatus.job.status === 'failed';

  return (
    <Dialog open={isOpen} onOpenChange={isComplete ? onClose : undefined}>
      <DialogContent className="w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Merging Accounts</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 overflow-y-auto pr-1">
          {jobStatus.minorProfiles.length > 0 && (
            <div className="rounded-lg border p-3 bg-gray-50 space-y-1">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Minor account{jobStatus.minorProfiles.length > 1 ? 's' : ''} (now empty)
              </div>
              {jobStatus.minorProfiles.map((profile) => (
                <div key={profile.id} className="text-sm text-gray-700 truncate">
                  {profile.full_name || 'No name'}
                  {profile.email && <span className="text-gray-500"> • <CopyableEmail email={profile.email} /></span>}
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2 text-sm">
              <span className="text-gray-600">Overall Progress</span>
              <span className="font-medium whitespace-nowrap flex-shrink-0">
                {jobStatus.progress.completed + jobStatus.progress.failed}/{jobStatus.progress.total}
              </span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-blue-600">{jobStatus.progress.completed}</div>
              <div className="text-xs text-gray-600 mt-1">Completed</div>
            </div>
            <div className="bg-amber-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-amber-600">{jobStatus.progress.pending}</div>
              <div className="text-xs text-gray-600 mt-1">Pending</div>
            </div>
            <div className="bg-red-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-red-600">{jobStatus.progress.failed}</div>
              <div className="text-xs text-gray-600 mt-1">Failed</div>
            </div>
          </div>

          {isComplete && (
            <div
              className={`p-3 rounded text-sm font-medium ${
                jobStatus.job.status === 'completed' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
              }`}
            >
              {jobStatus.job.status === 'completed'
                ? '✓ Merge completed. Minor accounts are now empty — delete them separately once reviewed.'
                : '✗ Merge completed with errors. Review failed tables below.'}
            </div>
          )}

          <div className="space-y-2">
            {jobStatus.items.map((item) => (
              <div key={item.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded text-sm">
                {item.status === 'completed' && <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />}
                {item.status === 'failed' && <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />}
                {item.status === 'pending' && <Loader2 className="h-4 w-4 text-blue-500 flex-shrink-0 animate-spin" />}

                <div className="flex-1 min-w-0">
                  <p className="text-gray-700 truncate">
                    {item.table_name}
                    {typeof item.rows_reassigned === 'number' && item.status === 'completed' && (
                      <span className="text-gray-400">
                        {' '}
                        ({item.rows_reassigned} reassigned
                        {item.rows_deduped ? `, ${item.rows_deduped} deduped` : ''})
                      </span>
                    )}
                  </p>
                  {item.error && <p className="text-xs text-red-600 break-words">{item.error}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {isComplete && (
          <div className="flex justify-end gap-2 pt-4 border-t flex-shrink-0">
            {jobStatus.job.status === 'failed' && (
              <Button
                variant="outline"
                size="sm"
                disabled={retryMutation.isPending}
                onClick={() => jobId && retryMutation.mutate(jobId)}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${retryMutation.isPending ? 'animate-spin' : ''}`} />
                {retryMutation.isPending ? 'Retrying…' : 'Retry Failed Tables'}
              </Button>
            )}
            <Button size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
